import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sun, Thermometer, Battery, Truck, Droplet, Wind, Zap, Hammer, Settings, ArrowRight, HardHat } from 'lucide-react';

const services = [
{
  icon: Sun,
  title: 'Zonnepanelen specialisten',
  description: 'Vakmensen voor professionele installatie van zonnepanelen',
  serviceArea: 'Zonnepanelen',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b805fb16-1767258690833.png",
  alt: 'Solar panel installation workers on a rooftop with blue sky',
  colSpan: 'lg:col-span-2'
},
{
  icon: Thermometer,
  title: 'Warmtepomp monteurs',
  description: 'Geschoold personeel voor warmtepomp installaties',
  serviceArea: 'Warmtepomp',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15d304572-1772284013829.png",
  alt: 'HVAC technician installing a heat pump unit outdoors',
  colSpan: 'lg:col-span-1'
},
{
  icon: Battery,
  title: 'Batterijsysteem technici',
  description: 'Specialisten voor thuisbatterij installatie en integratie',
  serviceArea: 'Batterijsysteem',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bdbe0fac-1766588301098.png",
  alt: 'Technician working on battery storage system in a modern facility',
  colSpan: 'lg:col-span-1'
},
{
  icon: HardHat,
  title: 'Grondwerken specialisten',
  description: 'Ervaren vakmensen voor grondverzet en funderingswerkzaamheden',
  serviceArea: 'Stratenmakers & Grondwerkers',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c575a9ef-1767760737700.png",
  alt: 'Construction workers operating heavy machinery on a building site',
  colSpan: 'lg:col-span-2'
},
{
  icon: Truck,
  title: 'Vrachtwagen chauffeurs',
  description: 'Professionele chauffeurs voor transport en logistiek',
  serviceArea: 'Vrachtwagen Chauffeurs',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1375ba1f6-1766873411701.png",
  alt: 'Professional truck driver in cab of large freight vehicle on highway',
  colSpan: 'lg:col-span-1'
},
{
  icon: Droplet,
  title: 'Loodgieter werkzaamheden',
  description: 'Gekwalificeerde loodgieters voor sanitair en leidingwerk',
  serviceArea: 'Loodgieters',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1db356c37-1772619620994.png",
  alt: 'Plumber working on pipes under a sink in a residential bathroom',
  colSpan: 'lg:col-span-2'
},
{
  icon: Zap,
  title: 'Elektromonteurs',
  description: 'Vakmensen voor elektrische aansluitingen en werkzaamheden',
  serviceArea: 'Elektrische Installatie',
  image: "https://images.unsplash.com/photo-1615774925655-a0e97fc85c14",
  alt: 'Electrician working on electrical panel with safety equipment',
  colSpan: 'lg:col-span-2'
},
{
  icon: Wind,
  title: 'Ventilatie specialisten',
  description: 'Personeel voor ventilatiesystemen en klimaatbeheersing',
  serviceArea: 'HVAC Systemen',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16f27fc35-1772066159428.png",
  alt: 'HVAC specialist installing ventilation ducts in a commercial building',
  colSpan: 'lg:col-span-1'
},
{
  icon: Hammer,
  title: 'Stratenmakers',
  description: 'Vakmensen voor bestrating en verhardingswerkzaamheden',
  serviceArea: 'Stratenmakers & Grondwerkers',
  image: "https://images.unsplash.com/photo-1607263419304-57d2a795a05e",
  alt: 'Road construction workers laying cobblestones on a city street',
  colSpan: 'lg:col-span-1'
},
{
  icon: Settings,
  title: 'Onderhoudsmonteurs',
  description: 'Vakmensen voor preventief en correctief onderhoud',
  serviceArea: 'Onderhoud',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a05804be-1772172045809.png",
  alt: 'Maintenance technician inspecting industrial machinery in a factory',
  colSpan: 'lg:col-span-2'
}];


const ServiceShowcase = () => {
  const navigate = useNavigate();

  const handleServiceClick = (serviceArea) => {
    navigate(`/login?serviceArea=${encodeURIComponent(serviceArea)}`);
  };

  return (
    <section id="services" className="py-20 md:py-28 bg-[#0A0A0A] relative overflow-hidden">
      {/* Ambient glow */}
      <div
        className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-[140px] opacity-5 pointer-events-none"
        style={{ background: '#00E5A0' }} />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          className="mb-14"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}>
          
          <p
            className="text-xs font-medium tracking-[0.2em] uppercase mb-4"
            style={{ color: '#00E5A0' }}>
            
            — Vakgebieden
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Beschikbaar personeel per vakgebied
          </h2>
          <p className="text-base max-w-xl leading-relaxed" style={{ color: '#888888' }}>
            Van zonnepanelen tot warmtepompen — vind de juiste vakman voor uw project
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {services?.map((service, index) => {
            const IconComponent = service?.icon;
            const isLarge = service?.colSpan === 'lg:col-span-2';

            return (
              <motion.div
                key={index}
                className={`relative overflow-hidden rounded-xl cursor-pointer group ${service?.colSpan} ${
                isLarge ? 'aspect-auto min-h-[260px]' : 'aspect-square'}`
                }
                style={{ background: '#111111', border: '1px solid #1A1A1A' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06, duration: 0.5 }}
                onClick={() => handleServiceClick(service?.serviceArea)}
                whileHover={{ borderColor: 'rgba(0,229,160,0.3)' }}>
                {/* Background image */}
                <div className="absolute inset-0">
                  <motion.img
                    src={service?.image}
                    alt={service?.alt}
                    className="w-full h-full object-cover"
                    style={{ filter: 'brightness(0.3) saturate(0.5)' }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.6 }} />
                  
                  {/* Gradient overlay */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                      'linear-gradient(to top, rgba(10,10,10,0.95) 0%, rgba(10,10,10,0.5) 50%, rgba(10,10,10,0.2) 100%)'
                    }} />
                  
                </div>
                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-between p-6">
                  {/* Icon top-left */}
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center self-start"
                    style={{
                      background: 'rgba(0,229,160,0.12)',
                      border: '1px solid rgba(0,229,160,0.25)'
                    }}>
                    
                    {IconComponent &&
                    <IconComponent
                      size={20}
                      strokeWidth={1.5}
                      style={{ color: '#00E5A0' }} />

                    }
                  </div>

                  {/* Text bottom */}
                  <div className="flex flex-col gap-1">
                    <h3
                      className="text-lg font-semibold text-white leading-snug group-hover:text-[#00E5A0] transition-colors duration-200">
                      
                      {service?.title}
                    </h3>
                    <p className="text-sm leading-relaxed mb-3" style={{ color: '#888888' }}>
                      {service?.description}
                    </p>
                    <div
                      className="inline-flex items-center gap-2 text-xs font-medium"
                      style={{ color: '#00E5A0' }}>
                      
                      <span>Bekijk vakmensen</span>
                      <ArrowRight size={13} strokeWidth={2} style={{ color: '#00E5A0' }} />
                    </div>
                  </div>
                </div>
              </motion.div>
            );

          })}
        </div>

        {/* Footer link */}
        <motion.div
          className="mt-8 p-8 text-center rounded-xl"
          style={{ background: '#0D0D0D', border: '1px solid #1A1A1A' }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}>
          
          <p className="text-sm mb-3" style={{ color: '#666666' }}>
            Niet gevonden wat je zoekt?
          </p>
          <a
            href="#finder"
            className="inline-flex items-center gap-2 text-sm font-medium transition-colors hover:text-white"
            style={{ color: '#00E5A0' }}>
            
            Gebruik onze personeel zoeker
            <ArrowRight size={15} strokeWidth={2} style={{ color: '#00E5A0' }} />
          </a>
        </motion.div>
      </div>
    </section>
  );

};

export default ServiceShowcase;